import { useEffect, useMemo, useState } from 'react';

import Price from 'types/Price';
import { getLeaselinkCartCalculationRoute, getLeaselinkVariantCalculationRoute } from 'apiRoutes/leaselink';
import LeaseLinkLogo from 'assets/icons/leaselink.svg';
import ArrowIcon from 'assets/icons/arrow.svg';
import AmountFormatter from '@misc/AmountFormatter';
import Typography from '@components/atoms/Typography';
import Loader from '@components/atoms/Loader';
import MessageWithBold from '@components/atoms/MessageWithBold';
import SkeletonLoader from '@components/atoms/SkeletonLoader';
import LeaselinkCalculator from '@components/LeaselinkCalculator';
import Modal from '@components/Modal';
import BackButton from '@components/BackButton';
import { FormattedMessage } from 'utils/intl';
import useBoolean from 'hooks/useBoolean';
import useFetch from 'hooks/useFetch';

import classes from './LeaseLinkBar.module.scss';
import messages from './LeaseLinkBar.messages';

type Props = {
  id?: number;
  type: 'inline' | 'box';
  isAvailable: boolean;
  withCalculator?: boolean;
  minAmount: Price;
};

const LeaseLinkBar = ({ id, type, isAvailable, withCalculator, minAmount }: Props) => {
  const [isOpen, { on: open, off: close, setValue: setIsOpen }] = useBoolean(false);

  const [activeLeasingId, setActiveLeasingId] = useState<string>();
  const [, { data: singleData, loading }] = useFetch(getLeaselinkVariantCalculationRoute, {
    payload: { id: id as number, multiOffer: false },
    manual: !isAvailable || !id,
  });
  const [, { data: cartData, loading: cartLoading }] = useFetch(getLeaselinkCartCalculationRoute, {
    payload: { multiOffer: false },
    manual: !isAvailable || !!id,
  });
  const [getLeaselinkCalculation, { data }] = useFetch(getLeaselinkVariantCalculationRoute);

  useEffect(() => {
    if (isAvailable && !data && id && isOpen) {
      getLeaselinkCalculation({ id, multiOffer: true });
    }
  }, [isAvailable, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeLeasingId && data) {
      setActiveLeasingId(data.Offers[0].CalculationPackageId);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const leasingPriceFrom = useMemo(
    () => (cartData?.Offers?.[0].GrossMonthlyInstallment || singleData?.Offers?.[0].GrossMonthlyInstallment || 0) * 100,
    [cartData, singleData]
  );

  return isAvailable && !leasingPriceFrom ? (
    <div className={classes.skeletonWrapper}>
      {(loading || cartLoading) && <SkeletonLoader iconWidth={650} iconHeight={20} background="transparent" />}
    </div>
  ) : (
    <>
      <div className={classes[type]}>
        <div className={classes.flexBox}>
          <LeaseLinkLogo height={16} />
          <Typography variant="caption1" className={classes.priceFrom}>
            <MessageWithBold
              message={isAvailable ? messages.priceFrom : messages.availableFrom}
              values={{
                price: (
                  <AmountFormatter
                    price={isAvailable ? { amount: leasingPriceFrom, currency: 'PLN' } : minAmount}
                    type="gross"
                  />
                ),
              }}
            />
          </Typography>
        </div>
        {isAvailable && withCalculator && (
          <Typography variant="caption1" className={classes.calculator} onClick={open}>
            <FormattedMessage {...messages.calculator} />
            <ArrowIcon height={10} />
          </Typography>
        )}
      </div>
      {withCalculator && !!id && (
        <Modal
          controlledIsOpen={isOpen}
          classes={{ box: classes.modal }}
          title={
            <div className={classes.modalHeader}>
              <LeaseLinkLogo height={24} />
              <Typography variant="h2" className={classes.modalHeaderText}>
                <FormattedMessage {...messages.calculator} />
              </Typography>
            </div>
          }
          onOpenChange={setIsOpen}
          withMobileClose
          hideWhenClickOutside
          smallClose
        >
          <div className={classes.modalContent}>
            {data ? (
              <LeaselinkCalculator
                offers={data.Offers}
                activeLeasingId={activeLeasingId}
                setActiveLeasingOffer={(offer) => setActiveLeasingId(offer?.CalculationPackageId)}
                showSingleFinancialType
              />
            ) : (
              <div className={classes.loaderWrapper}>
                <Loader size={40} isDark />
              </div>
            )}
            <BackButton onClick={close} />
          </div>
        </Modal>
      )}
    </>
  );
};

export default LeaseLinkBar;
